// Shared ActionCable helpers using Turbo's built-in cable consumer.
//
// All subscriptions — turbo-cable-stream-source elements AND Stimulus
// controllers — share the same consumer from @hotwired/turbo-rails via
// cable.getConsumer(). This ensures a single WebSocket connection.
//
// turbo-rails 8.x exposes getConsumer() as async (dynamic import of
// @rails/actioncable/src). subscribe() wraps this async acquisition in a
// synchronous handle so callers can call .unsubscribe() in disconnect()
// without needing to await anything.

import { cable } from "@hotwired/turbo-rails"

export { cable }

// Reads the current tenant id from the meta tag rendered in the layout.
export function tenantId() {
  return document.querySelector('meta[name="tenant-id"]')?.content || null
}

// Creates a subscription on the shared consumer.
//
//   const sub = subscribe(
//     { channel: "Chat::TypingChannel", room_id: 1 },
//     { received: (data) => {...} }
//   )
//
// tenant_id is added automatically unless the caller already provided one.
// Returns a handle with .unsubscribe() and .perform() that are safe to call
// at any time, even before the async consumer acquisition has resolved.
export function subscribe(params, handlers = {}) {
  const fullParams = { tenant_id: tenantId(), ...params }
  // Drop a null tenant_id so the server-side identifier stays clean.
  if (fullParams.tenant_id == null) delete fullParams.tenant_id

  let resolvedSub = null
  let pendingUnsubscribe = false
  const pendingPerforms = []

  cable.getConsumer().then(consumer => {
    if (pendingUnsubscribe) return
    resolvedSub = consumer.subscriptions.create(fullParams, handlers)
    // Flush any perform() calls that arrived before the sub was ready.
    pendingPerforms.forEach(({ action, data }) => resolvedSub.perform(action, data))
    pendingPerforms.length = 0
  })

  return {
    unsubscribe() {
      if (resolvedSub) {
        resolvedSub.unsubscribe()
      } else {
        pendingUnsubscribe = true
      }
    },
    perform(action, data) {
      if (resolvedSub) {
        resolvedSub.perform(action, data)
      } else if (!pendingUnsubscribe) {
        pendingPerforms.push({ action, data })
      }
    },
  }
}

export default { cable, subscribe, tenantId }
